import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  Banknote,
  Calculator,
  ChevronRight,
  ChevronsUpDown,
  House,
  LogOut,
  type LucideIcon,
  Menu,
  MessageCircleQuestionMark,
  Shield,
  Ticket,
  Truck,
  User,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { signOut } from '@/api/auth';
import { queryClient } from '@/api/queries.utils';
import Logo from '@/assets/logo.png';
import { useAuth } from '@/context/useAuth';
import { useCommiteeSlugFromUrl } from '@/utils/router';
import { Button } from './Button';
import { CompanySelector } from './CompanySelector';
import { SpacingWidth } from './icons/SpacingWidth';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './shadcn/Collapsible';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/Popover';
import {
  ShadcnSidebar,
  ShadcnSidebarContent,
  ShadcnSidebarFooter,
  ShadcnSidebarGroup,
  ShadcnSidebarGroupContent,
  ShadcnSidebarHeader,
  ShadcnSidebarMenu,
  ShadcnSidebarMenuButton,
  ShadcnSidebarMenuItem,
  ShadcnSidebarMenuSub,
  ShadcnSidebarMenuSubButton,
  ShadcnSidebarMenuSubItem,
  ShadcnSidebarProvider,
  useSidebar,
} from './shadcn/Sidebar';

type SidebarItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  items?: SidebarItem[];
  isActive?: boolean;
};

type SidebarData = {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  navMain: SidebarItem[];
  navSecondary: SidebarItem[];
};

const getSidebarData = (commiteeSlug: string, isAdmin: boolean): SidebarData => ({
  user: {
    name: 'Name',
    email: 'name@mail.com',
    avatar: '/avatars/shadcn.jpg',
  },
  navMain: [
    {
      title: 'Tableau de bord',
      url: `/${commiteeSlug}`,
      icon: House,
      isActive: true,
    },
    {
      title: 'Bénéficiaires',
      url: '#',
      icon: Users,
      items: [
        {
          title: 'Salariés',
          url: `/${commiteeSlug}/beneficiaries/employees`,
        },
        {
          title: 'Ayants-droit',
          url: `/${commiteeSlug}/beneficiaries/employees-beneficiaries`,
        },
        {
          title: 'Dettes & Créances',
          url: `/${commiteeSlug}/beneficiaries/debts`,
        },
        {
          title: 'Règlements',
          url: `/${commiteeSlug}/beneficiaries/transactions`,
        },
        {
          title: 'Commandes Pass CE',
          url: `/${commiteeSlug}/beneficiaries/billetterie-orders`,
        },
      ],
    },
    {
      title: 'Prestations',
      url: '#',
      icon: Ticket,
      items: [
        {
          title: 'Prestations',
          url: `/${commiteeSlug}/prestation`,
        },
        {
          title: 'Liste des dossiers',
          url: `/${commiteeSlug}/prestation-participations`,
        },
        {
          title: 'Règles',
          url: `/${commiteeSlug}/prestation-rules`,
        },
      ],
    },
    {
      title: 'Fournisseurs',
      url: '#',
      icon: Truck,
      items: [
        {
          title: 'Fournisseurs',
          url: `/${commiteeSlug}/suppliers`,
        },
        {
          title: 'Commandes',
          url: `/${commiteeSlug}/orders`,
        },
        {
          title: 'Factures',
          url: `/${commiteeSlug}/invoices`,
        },
        {
          title: 'État des stocks',
          url: `/${commiteeSlug}/inventory`,
        },
      ],
    },
    {
      title: 'Comptabilité',
      url: '#',
      icon: Calculator,
      items: [
        {
          title: 'Liste des écritures',
          url: `/${commiteeSlug}/accounting/transactions`,
        },
        {
          title: 'Rapprochement bancaire',
          url: `/${commiteeSlug}/accounting/bank-reconciliations`,
        },
        {
          title: 'Immobilisation',
          url: `/${commiteeSlug}/immobilisations`,
        },
        {
          title: 'Etats comptables',
          url: `/${commiteeSlug}/accounting/reports`,
        },
        {
          title: 'Fichier des écritures comptables',
          url: `/${commiteeSlug}/accounting/fec`,
        },
        {
          title: 'Cloture',
          url: `/${commiteeSlug}/accounting/closing`,
        },
        {
          title: 'Paramètres',
          url: `/${commiteeSlug}/accounting/parameters`,
        },
      ],
    },
    {
      title: 'Trésorerie',
      url: '#',
      icon: Banknote,
      items: [
        {
          title: 'Règlements à remettre',
          url: `/${commiteeSlug}/treasury/pending-payments`,
        },
        {
          title: 'Décaissements à remettre',
          url: `/${commiteeSlug}/treasury/pending-disbursements`,
        },
        {
          title: 'Historique des remises',
          url: `/${commiteeSlug}/treasury/bank-deposits`,
        },
        {
          title: 'Caisse',
          url: `/${commiteeSlug}/treasury/cash-register`,
        },
      ],
    },
  ],
  navSecondary: [
    ...(isAdmin
      ? [
          {
            title: 'Gestion des entreprises',
            url: '/gestion/general',
            icon: Shield,
          },
        ]
      : []),
    {
      title: 'Support',
      url: '#',
      icon: MessageCircleQuestionMark,
    },
  ],
});

function SidebarToggleButton() {
  const { toggleSidebar } = useSidebar();
  return <Button variant="outline" icon={SpacingWidth} onClick={toggleSidebar} />;
}

function CollapsibleMenuItem({
  item,
  isActive,
  isOpen,
  onOpenChange,
  pathName,
}: {
  item: SidebarItem;
  isActive: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  pathName: string;
}) {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  if (isCollapsed) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <ShadcnSidebarMenuButton tooltip={item.title} isActive={isActive}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto" />
          </ShadcnSidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-48 p-1">
          <div className="flex flex-col gap-1">
            {item.items?.map((subItem) => {
              const isSubItemActive =
                pathName === subItem.url ||
                (pathName.startsWith(`${subItem.url}/`) && pathName.length > subItem.url.length);
              return (
                <Link
                  key={subItem.title}
                  to={subItem.url}
                  className={`rounded-md px-2 py-1.5 text-xs hover:bg-gray-100 ${isSubItemActive ? 'bg-gray-900 text-white hover:bg-gray-900' : ''}`}
                >
                  {subItem.title}
                </Link>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Collapsible asChild open={isOpen} onOpenChange={onOpenChange} className="group/collapsible">
      <ShadcnSidebarMenuItem>
        <CollapsibleTrigger asChild>
          <ShadcnSidebarMenuButton tooltip={item.title} isActive={isActive}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </ShadcnSidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ShadcnSidebarMenuSub>
            {item.items?.map((subItem) => {
              const isSubItemActive =
                pathName === subItem.url ||
                (pathName.startsWith(`${subItem.url}/`) && pathName.length > subItem.url.length);
              return (
                <ShadcnSidebarMenuSubItem key={subItem.title}>
                  <ShadcnSidebarMenuSubButton asChild isActive={isSubItemActive}>
                    <Link to={subItem.url}>
                      <span>{subItem.title}</span>
                    </Link>
                  </ShadcnSidebarMenuSubButton>
                </ShadcnSidebarMenuSubItem>
              );
            })}
          </ShadcnSidebarMenuSub>
        </CollapsibleContent>
      </ShadcnSidebarMenuItem>
    </Collapsible>
  );
}

function MobileNav({
  data,
  pathName,
  isAdmin,
  commiteeSlugFromUrl,
  session,
  handleLogout,
  navigate,
}: {
  data: SidebarData;
  pathName: string;
  isAdmin: boolean;
  commiteeSlugFromUrl: string | undefined;
  session: ReturnType<typeof useAuth>['session'];
  handleLogout: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const isDisabled = isAdmin && !commiteeSlugFromUrl;

  return (
    <div className="md:hidden">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4">
        <img src={Logo} alt="Logo" className="h-8" />
        <Button variant="outline" icon={isOpen ? X : Menu} onClick={() => setIsOpen(!isOpen)} />
      </div>

      {/* Full-screen overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-white pt-14 overflow-y-auto">
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4">
              <CompanySelector isAdmin={isAdmin} />
              <nav className={`mt-4 ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <ul className="flex flex-col gap-1">
                  {data.navMain.map((item) => {
                    const isActive =
                      item.items?.some((subItem) => pathName.startsWith(subItem.url)) ||
                      item.isActive ||
                      false;
                    const isMenuOpen = openMenus[item.title] ?? isActive;

                    return (
                      <li key={item.title}>
                        {item.items?.length ? (
                          <Collapsible
                            open={isMenuOpen}
                            onOpenChange={(open) =>
                              setOpenMenus((prev) => ({ ...prev, [item.title]: open }))
                            }
                          >
                            <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-md p-3 text-sm font-medium hover:bg-gray-50">
                              {item.icon && <item.icon className="size-5" />}
                              <span>{item.title}</span>
                              <ChevronRight
                                className={`ml-auto size-4 transition-transform ${isMenuOpen ? 'rotate-90' : ''}`}
                              />
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <ul className="ml-7 flex flex-col gap-1 border-l border-gray-200 pl-3 py-1">
                                {item.items.map((subItem) => {
                                  const isSubItemActive =
                                    pathName === subItem.url ||
                                    (pathName.startsWith(`${subItem.url}/`) &&
                                      pathName.length > subItem.url.length);
                                  return (
                                    <li key={subItem.title}>
                                      <Link
                                        to={subItem.url}
                                        onClick={() => setIsOpen(false)}
                                        className={`block rounded-md px-3 py-2 text-sm ${isSubItemActive ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                                      >
                                        {subItem.title}
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </CollapsibleContent>
                          </Collapsible>
                        ) : (
                          <Link
                            to={item.url}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-2 rounded-md p-3 text-sm font-medium ${pathName === item.url ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                          >
                            {item.icon && <item.icon className="size-5" />}
                            <span>{item.title}</span>
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {/* Secondary nav */}
                <ul className="mt-6 flex flex-col gap-1 border-t border-gray-200 pt-4">
                  {data.navSecondary.map((item) => (
                    <li key={item.title}>
                      <Link
                        to={item.url}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 rounded-md p-3 text-sm hover:bg-gray-50"
                      >
                        {item.icon && <item.icon className="size-5" />}
                        <span>{item.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate({ to: '/account/details' });
                }}
                className="flex w-full cursor-pointer items-center gap-3 rounded-md p-3 hover:bg-gray-50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-200">
                  <User className="size-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold">
                    {session?.user?.name || data.user.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {session?.user?.email || data.user.email}
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="mt-2 flex w-full cursor-pointer items-center gap-2 rounded-md p-3 text-sm font-medium hover:bg-gray-50"
              >
                <LogOut className="size-5" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  // TODO: Positionnement du bouton deconnecter temporaire
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const pathName = useMemo(() => location.pathname, [location.pathname]);

  const commiteeSlugFromUrl = useCommiteeSlugFromUrl();
  const commiteeSlug = commiteeSlugFromUrl || session?.user?.commiteeSlug || '';

  const { isAdmin } = useAuth();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const isDisabled = isAdmin && !commiteeSlugFromUrl;

  const data = useMemo(() => getSidebarData(commiteeSlug, isAdmin), [commiteeSlug, isAdmin]);

  useEffect(() => {
    if (isAdmin && !commiteeSlugFromUrl) {
      setOpenMenus({});
    }
  }, [isAdmin, commiteeSlugFromUrl]);

  const handleLogout = async () => {
    queryClient.cancelQueries();
    await signOut();
    queryClient.clear();
    window.location.href = '/login';
  };

  return (
    <ShadcnSidebarProvider>
      <MobileNav
        data={data}
        pathName={pathName}
        isAdmin={isAdmin}
        commiteeSlugFromUrl={commiteeSlugFromUrl}
        session={session}
        handleLogout={handleLogout}
        navigate={navigate}
      />
      <ShadcnSidebar collapsible="icon" className="text-gray-700">
        <ShadcnSidebarHeader className="flex flex-row items-center justify-between">
          <img src={Logo} alt="Logo" className="w-25 group-data-[collapsible=icon]:hidden" />
          <SidebarToggleButton />
        </ShadcnSidebarHeader>
        <ShadcnSidebarContent>
          <CompanySelector isAdmin={isAdmin} />
          <ShadcnSidebarGroup className={isDisabled ? 'opacity-50 pointer-events-none' : ''}>
            <ShadcnSidebarGroupContent>
              <ShadcnSidebarMenu>
                {data.navMain.map((item) => {
                  const isActive =
                    item.items?.some((subItem) => pathName.startsWith(subItem.url)) ||
                    item.isActive ||
                    false;
                  const shouldBeOpenByDefault = isAdmin && !commiteeSlugFromUrl ? false : isActive;
                  const isOpen = openMenus[item.title] ?? shouldBeOpenByDefault;

                  return (
                    <ShadcnSidebarMenu key={item.title}>
                      {item.items?.length ? (
                        <CollapsibleMenuItem
                          item={item}
                          isActive={isActive}
                          isOpen={isOpen}
                          onOpenChange={(open) => {
                            setOpenMenus((prev) => ({ ...prev, [item.title]: open }));
                          }}
                          pathName={pathName}
                        />
                      ) : (
                        <ShadcnSidebarMenuButton
                          asChild
                          tooltip={item.title}
                          isActive={pathName === item.url}
                        >
                          <Link to={item.url}>
                            {item.icon && <item.icon />}
                            <span>{item.title}</span>
                          </Link>
                        </ShadcnSidebarMenuButton>
                      )}
                    </ShadcnSidebarMenu>
                  );
                })}
              </ShadcnSidebarMenu>
            </ShadcnSidebarGroupContent>
          </ShadcnSidebarGroup>

          <ShadcnSidebarGroup className="mt-auto">
            <ShadcnSidebarGroupContent>
              <ShadcnSidebarMenu>
                {data.navSecondary.map((item) => (
                  <ShadcnSidebarMenuItem key={item.title}>
                    <ShadcnSidebarMenuButton asChild size="sm">
                      <Link to={item.url}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                      </Link>
                    </ShadcnSidebarMenuButton>
                  </ShadcnSidebarMenuItem>
                ))}
              </ShadcnSidebarMenu>
            </ShadcnSidebarGroupContent>
          </ShadcnSidebarGroup>
        </ShadcnSidebarContent>

        <ShadcnSidebarFooter>
          <ShadcnSidebarMenu>
            <ShadcnSidebarMenuItem>
              <ShadcnSidebarMenuButton
                size="lg"
                tooltip="Mon compte"
                onClick={() => {
                  navigate({
                    to: '/account/details',
                  });
                }}
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="h-8 w-8 shrink-0 rounded-lg bg-gray-200 flex items-center justify-center">
                  <User className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-xs leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">
                    {session?.user?.name || data.user.name}
                  </span>
                  <span className="truncate text-xs font-normal">
                    {session?.user?.email || data.user.email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
              </ShadcnSidebarMenuButton>
            </ShadcnSidebarMenuItem>
            {/* Emplacement temporaire */}
            <ShadcnSidebarMenuItem>
              <ShadcnSidebarMenuButton
                size="sm"
                tooltip="Déconnexion"
                onClick={handleLogout}
                className="flex items-center justify-center gap-2"
              >
                <LogOut className="size-4" />
                <span className="font-semibold group-data-[collapsible=icon]:hidden">
                  Déconnexion
                </span>
              </ShadcnSidebarMenuButton>
            </ShadcnSidebarMenuItem>
          </ShadcnSidebarMenu>
        </ShadcnSidebarFooter>
      </ShadcnSidebar>
    </ShadcnSidebarProvider>
  );
}
